<template>
  <section
    class="service-page"
    :class="{ loaded: pageLoaded }"
  >

    <!-- HERO -->
    <section class="hero">

      <div class="hero-content">

        <span class="eyebrow">
          Branding • Estrategia • Identidad Visual
        </span>

        <h1 class="hero-title">
          Branding y Estrategia de Marca
        </h1>

        <p class="hero-description">
          Construimos marcas con dirección, personalidad y posicionamiento
          para que tu negocio comunique con claridad, coherencia y diferenciación
          desde el primer contacto.
        </p>

        <div class="hero-actions">

          <button
            class="primary-btn"
            type="button"
            :disabled="loading"
            @click="goToServiceForm"
          >
            {{ loading ? "Cargando..." : "Construir Mi Marca" }}
          </button>

          <div class="hero-badges">

            <span>Identidad Visual</span>
            <span>Posicionamiento</span>
            <span>Storytelling</span>
            <span>Brand Strategy</span>

          </div>

        </div>

      </div>

      <div class="hero-image">

        <img
          src="/images/img17.jpg"
          alt="Branding e identidad visual"
          loading="lazy"
          decoding="async"
        />

      </div>

    </section>

    <!-- PROBLEMS -->
    <section class="problems">

      <div class="section-heading">

        <span class="section-label">
          Problemas comunes
        </span>

        <h2>
          Lo que debilita una marca
        </h2>

      </div>

      <div class="problem-grid">

        <article
          v-for="(problem, i) in problems"
          :key="i"
          class="problem-card"
        >

          <span class="problem-icon">
            ✕
          </span>

          <p>
            {{ problem }}
          </p>

        </article>

      </div>

    </section>

    <!-- SOLUTIONS -->
    <section class="solutions">

      <div class="section-heading">

        <span class="section-label">
          Soluciones incluidas
        </span>

        <h2>
          Construcción estratégica de marca
        </h2>

      </div>

      <div class="solution-grid">

        <article
          v-for="solution in solutions"
          :key="solution.title"
          class="solution-card"
        >

          <div class="solution-icon">
            {{ solution.icon }}
          </div>

          <h3>
            {{ solution.title }}
          </h3>

          <p>
            {{ solution.description }}
          </p>

          <ul>

            <li
              v-for="(item, i) in solution.features"
              :key="i"
            >
              ✓ {{ item }}
            </li>

          </ul>

        </article>

      </div>

    </section>

    <!-- MICRO SERVICES -->
    <section class="microservices">

      <div class="section-heading">

        <span class="section-label">
          Microservicios
        </span>

        <h2>
          Servicios específicos
        </h2>

      </div>

      <div class="micro-grid">

        <article
          v-for="service in microservices"
          :key="service.title"
          class="micro-card"
        >

          <h3>
            {{ service.title }}
          </h3>

          <p>
            {{ service.description }}
          </p>

          <span class="price">
            {{ service.price }}
          </span>

        </article>

      </div>

    </section>

    <!-- BENEFITS -->
    <section class="benefits">

      <div class="section-heading center">

        <span class="section-label">
          Beneficios
        </span>

        <h2>
          Lo que obtiene tu negocio
        </h2>

      </div>

      <div class="benefit-grid">

        <article
          v-for="(benefit, i) in benefits"
          :key="i"
          class="benefit-card"
        >

          <span>
            ✓
          </span>

          <p>
            {{ benefit }}
          </p>

        </article>

      </div>

    </section>

    <!-- PROCESS -->
    <section class="process">

      <div class="section-heading center">

        <span class="section-label">
          Nuestro proceso
        </span>

        <h2>
          Cómo desarrollamos tu marca
        </h2>

      </div>

      <div class="process-grid">

        <article
          v-for="step in process"
          :key="step.number"
          class="process-card"
        >

          <span class="step-number">
            {{ step.number }}
          </span>

          <h3>
            {{ step.title }}
          </h3>

          <p>
            {{ step.description }}
          </p>

        </article>

      </div>

    </section>

    <!-- BRAND PLANS -->
    <section class="support-model">

      <div class="section-heading center">

        <span class="section-label">
          Modalidades
        </span>

        <h2>
          Formas de trabajo
        </h2>

      </div>

      <div class="support-grid">

        <article
          v-for="plan in brandPlans"
          :key="plan.title"
          class="support-card"
          :class="{ featured: plan.featured }"
        >

          <span
            v-if="plan.featured"
            class="badge"
          >
            Más solicitado
          </span>

          <h3>
            {{ plan.title }}
          </h3>

          <p class="support-description">
            {{ plan.description }}
          </p>

          <ul>

            <li
              v-for="(item, i) in plan.features"
              :key="i"
            >
              ✓ {{ item }}
            </li>

          </ul>

          <span class="support-price">
            {{ plan.price }}
          </span>

        </article>

      </div>

    </section>

    <!-- CTA -->
    <section class="cta-section">

      <div class="cta-card">

        <span class="cta-label">
          Diferenciación • Posicionamiento • Identidad
        </span>

        <h2>
          Tu marca debe verse tan profesional como tu negocio
        </h2>

        <p>
          Creamos marcas estratégicas y visualmente sólidas
          para ayudarte a conectar con mejores clientes
          y diferenciarte en el mercado.
        </p>

        <button
          class="primary-btn"
          type="button"
          :disabled="loading"
          @click="goToServiceForm"
        >
          {{ loading ? "Cargando..." : "Cotizar Branding" }}
        </button>

      </div>

    </section>

  </section>
</template>

<script>
import { useHead } from "@vueuse/head";

export default {
  name: "BrandingStrategyComponent",

  data() {
    return {
      loading: false,
      pageLoaded: false,

      problems: Object.freeze([
        "Marca sin dirección clara",
        "Identidad visual inconsistente",
        "Diseño poco profesional",
        "Falta de diferenciación",
        "Comunicación confusa",
        "No conectar con el cliente ideal"
      ]),

      solutions: Object.freeze([
  {
    icon: "🧠",
    title: "Estrategia de Marca",
    description: "Definimos quién eres, a quién le hablas y cómo te diferencias antes de diseñar cualquier cosa.",
    features: [
      "Naming y tagline",
      "Propósito y valores de marca",
      "Público objetivo y diferenciadores",
      "Posicionamiento",
      "Storytelling",
    ],
  },
  {
    icon: "🎨",
    title: "Identidad Visual",
    description: "Construimos el sistema visual que hace reconocible y profesional a tu marca en cualquier medio.",
    features: [
      "Logo y archivos editables",
      "Paleta de color y tipografías",
      "Papelería corporativa",
      "Branding para redes sociales",
      "Manual de marca completo",
    ],
  },
  {
    icon: "✨",
    title: "Branding Avanzado",
    description: "Llevamos tu marca al siguiente nivel con recursos visuales que destacan y se mueven.",
    features: [
      "Packaging",
      "Motion branding",
      "Microanimaciones 2D y 3D",
      "Diseño web visual",
      "Rebranding y consultoría",
    ],
  },
]),

      microservices: Object.freeze([
        {
          title: "Naming de Marca",
          description:
            "Creación de nombre comercial y validación inicial.",
        },
        {
          title: "Diseño de Logo",
          description:
            "Diseño visual profesional para tu marca.",
        },
        {
          title: "Manual de Marca",
          description:
            "Guía visual y reglas de uso de identidad.",
        },
        {
          title: "Branding Redes Sociales",
          description:
            "Diseño visual para perfiles y contenido.",
        },
        {
          title: "Packaging",
          description:
            "Diseño visual para empaques y presentación.",
        },
        {
          title: "Rebranding",
          description:
            "Renovación visual y estratégica de marca.",
        }
      ]),

      benefits: Object.freeze([
        "Mayor profesionalismo",
        "Marca más memorable",
        "Diferenciación clara",
        "Comunicación coherente",
        "Mayor confianza del cliente",
        "Mejor posicionamiento"
      ]),

      process: Object.freeze([
        {
          number: "01",
          title: "Descubrimiento",
          description:
            "Analizamos negocio, mercado y objetivos."
        },
        {
          number: "02",
          title: "Estrategia",
          description:
            "Definimos dirección, personalidad y posicionamiento."
        },
        {
          number: "03",
          title: "Diseño",
          description:
            "Creamos identidad visual y recursos gráficos."
        },
        {
          number: "04",
          title: "Implementación",
          description:
            "Adaptamos la marca a canales y materiales."
        }
      ]),

      brandPlans: Object.freeze([
        {
          title: "Brand Start",
          featured: false,
          description:
            "Base visual y estratégica para nuevos negocios.",
          features: [
            "Naming",
            "Logo",
            "Paleta visual",
            "Misión y visión"
          ]
        },
        {
          title: "Brand Professional",
          featured: true,
          description:
            "Desarrollo completo de identidad y posicionamiento.",
          features: [
            "Todo lo básico",
            "Buyer persona",
            "Storytelling",
            "Manual de marca",
            "Sistema visual"
          ]
        },
        {
          title: "Brand Premium",
          featured: false,
          description:
            "Branding avanzado y expansión visual.",
          features: [
            "Packaging",
            "Branding redes",
            "Diseño web",
            "Consultoría estratégica"
          ]
        }
      ])
    };
  },

  mounted() {
    requestAnimationFrame(() => {
      this.pageLoaded = true;
    });
  },

  setup() {
    useHead({
      title: "Branding y Estrategia de Marca | BearTech",
      htmlAttrs: {
        lang: "es-MX"
      },
      meta: [
        {
          name: "description",
          content:
            "Servicios de branding, identidad visual y estrategia de marca para negocios y empresas."
        }
      ]
    });
  },

  methods: {
    async goToServiceForm() {
      if (this.loading) return;

      this.loading = true;

      try {
        await this.$router.push(
          "/servicios/branding-identidad-marca/formulario"
        );
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>

/* ===== PAGE ENTRY ===== */

.service-page {
  opacity: 0;
  transform: translateY(30px);

  transition:
    opacity .8s ease,
    transform .8s ease;

  background:
    linear-gradient(
      180deg,
      #f8fafc 0%,
      #ffffff 100%
    );

  color: #0f172a;
}

.service-page.loaded {
  opacity: 1;
  transform: translateY(0);
}

/* ===== STAGGER ===== */

.hero-content,
.hero-image,
.section-heading,
.problem-card,
.solution-card,
.micro-card,
.benefit-card,
.process-card,
.support-card,
.cta-card {
  opacity: 0;
  transform: translateY(30px);
}

.loaded .hero-content {
  animation: fadeUp .8s ease forwards;
}

.loaded .hero-image {
  animation: fadeUp .9s ease .15s forwards;
}

.loaded .section-heading {
  animation: fadeUp .7s ease forwards;
}

.loaded .problem-card,
.loaded .solution-card,
.loaded .micro-card,
.loaded .benefit-card,
.loaded .process-card,
.loaded .support-card {
  animation: fadeUp .8s ease forwards;
}

.loaded .problem-card:nth-child(1),
.loaded .solution-card:nth-child(1),
.loaded .micro-card:nth-child(1),
.loaded .benefit-card:nth-child(1),
.loaded .process-card:nth-child(1),
.loaded .support-card:nth-child(1) {
  animation-delay: .1s;
}

.loaded .problem-card:nth-child(2),
.loaded .solution-card:nth-child(2),
.loaded .micro-card:nth-child(2),
.loaded .benefit-card:nth-child(2),
.loaded .process-card:nth-child(2),
.loaded .support-card:nth-child(2) {
  animation-delay: .2s;
}

.loaded .problem-card:nth-child(3),
.loaded .solution-card:nth-child(3),
.loaded .micro-card:nth-child(3),
.loaded .benefit-card:nth-child(3),
.loaded .process-card:nth-child(3),
.loaded .support-card:nth-child(3) {
  animation-delay: .3s;
}

.loaded .cta-card {
  animation: fadeScale 1s ease .2s forwards;
}

/* ===== GLOBAL ===== */

section {
  padding: 5rem 1.5rem;
}

.section-heading {
  margin-bottom: 3rem;
}

.section-heading.center {
  text-align: center;
}

.section-label {
  display: inline-block;

  margin-bottom: 1rem;
  padding: .45rem 1rem;

  border-radius: 999px;

  background: #ccfbf1;
  color: #0f766e;

  font-size: .85rem;
  font-weight: 700;
}

.section-heading h2 {
  font-size: clamp(2rem, 4vw, 3rem);
  color: #012719;
}

/* ===== HERO ===== */

.hero {
  max-width: 1300px;
  margin: auto;

  min-height: 90vh;

  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
}

.eyebrow {
  display: inline-block;
  margin-bottom: 1.5rem;

  color: #0f766e;
  font-weight: 700;
}

.hero-title {
  font-size: clamp(3rem, 7vw, 5rem);
  line-height: 1;

  margin-bottom: 1.5rem;

  color: #012719;
}

.hero-description {
  max-width: 600px;

  font-size: 1.15rem;
  line-height: 1.9;

  color: #475569;
}

.hero-actions {
  margin-top: 2rem;
}

.hero-badges {
  display: flex;
  flex-wrap: wrap;
  gap: .8rem;

  margin-top: 1.5rem;
}

.hero-badges span {
  padding: .6rem 1rem;

  border-radius: 999px;

  background: white;
  border: 1px solid #dbe4ea;

  font-size: .9rem;
}

.hero-image {
  overflow: hidden;
  border-radius: 30px;

  box-shadow:
    0 25px 60px rgba(0,0,0,.12);
}

.hero-image img {
  width: 100%;
  height: 750px;

  object-fit: cover;
  display: block;
}

/* ===== BUTTON ===== */


.primary-btn{
  position:relative;
  overflow:hidden;

  padding:1rem 2rem;

  border:none;
  border-radius:14px;

  background:
    linear-gradient(
      135deg,
      #051b19,
      #5a665e
    );

  color:white;
  font-weight:700;

  cursor:pointer;

  transition:
    transform .25s ease,
    opacity .25s ease,
    box-shadow .25s ease;
}

.primary-btn::before{
  content:"";

  position:absolute;
  top:0;
  left:-120%;

  width:100%;
  height:100%;

  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(255,255,255,.25),
      transparent
    );

  transition:left .6s ease;
}

.primary-btn:hover::before{
  left:120%;
}

.primary-btn:hover{
  transform:translateY(-4px);

  box-shadow:
    0 15px 30px rgba(15,118,110,.3);
}

.primary-btn:disabled{
  opacity:.6;
  cursor:not-allowed;
}

/* ===== GRIDS ===== */

.problems,
.solutions,
.microservices,
.benefits,
.process,
.support-model {
  max-width: 1300px;
  margin: auto;
}

.problem-grid,
.solution-grid,
.micro-grid,
.benefit-grid,
.process-grid,
.support-grid {
  display: grid;
  gap: 2rem;
}

.problem-grid,
.solution-grid,
.micro-grid,
.benefit-grid,
.support-grid {
  grid-template-columns: repeat(3, 1fr);
}

.process-grid {
  grid-template-columns: repeat(4, 1fr);
}

/* ===== CARDS ===== */

.problem-card,
.solution-card,
.micro-card,
.benefit-card,
.process-card,
.support-card {
  background: white;

  border-radius: 24px;

  padding: 2rem;

  border: 1px solid #e2e8f0;

  transition:
    transform .25s ease,
    box-shadow .25s ease;
}

.problem-card:hover,
.solution-card:hover,
.micro-card:hover,
.benefit-card:hover,
.process-card:hover,
.support-card:hover {
  transform: translateY(-5px);

  box-shadow:
    0 15px 35px rgba(0,0,0,.08);
}

/* ===== PROBLEMS ===== */

.problem-card {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.problem-icon {
  color: #ef4444;
  font-size: 1.4rem;
}

/* ===== SOLUTIONS ===== */

.solution-icon {
  font-size: 2rem;
  margin-bottom: 1rem;
}

.solution-card h3 {
  margin-bottom: 1rem;
  color: #012719;
}

.solution-card p {
  margin-bottom: 1.5rem;

  line-height: 1.7;
  color: #475569;
}

.solution-card ul {
  padding-left: 1rem;
}

.solution-card li {
  margin-bottom: .8rem;
}

/* ===== MICRO ===== */

.micro-card h3 {
  margin-bottom: 1rem;
}

.micro-card p {
  line-height: 1.7;
  color: #475569;
}

.price {
  display: inline-block;
  margin-top: 1.5rem;

  color: #0f766e;
  font-weight: 700;
}

/* ===== BENEFITS ===== */

.benefit-card {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.benefit-card span {
  color: #0f766e;
  font-size: 1.3rem;
}

/* ===== PROCESS ===== */

.step-number {
  display: inline-flex;

  width: 60px;
  height: 60px;

  align-items: center;
  justify-content: center;

  border-radius: 50%;

  background: #ccfbf1;
  color: #0f766e;

  font-weight: 800;

  margin-bottom: 1.5rem;
}

/* ===== SUPPORT ===== */

.support-card {
  position: relative;
}

.support-card.featured {
  border: 2px solid #0f766e;
}

.badge {
  position: absolute;

  top: -12px;
  left: 50%;

  transform: translateX(-50%);

  background: #0f766e;
  color: white;

  padding: .4rem 1rem;

  border-radius: 999px;

  font-size: .8rem;
  font-weight: 700;
}

.support-card h3 {
  margin-bottom: 1rem;
  color: #012719;
}

.support-description {
  margin-bottom: 1.5rem;

  line-height: 1.7;
  color: #475569;
}

.support-card ul {
  padding-left: 1rem;
}

.support-card li {
  margin-bottom: .8rem;
}

.support-price {
  display: inline-block;
  margin-top: 1.5rem;

  color: #0f766e;
  font-weight: 700;
}

/* ===== CTA ===== */

.cta-section {
  padding-bottom: 6rem;
}

.cta-card {
  max-width: 1100px;
  margin: auto;

  text-align: center;

  padding: 5rem 2rem;

  border-radius: 32px;

  background:
    linear-gradient(
      135deg,
      #012719,
      #0f766e
    );

  color: white;
}

.cta-card h2 {
  font-size: clamp(2rem, 5vw, 4rem);
  margin-bottom: 1rem;
}

.cta-card p {
  max-width: 700px;

  margin:
    auto
    auto
    2rem;

  line-height: 1.8;

  opacity: .9;
}

/* ===== KEYFRAMES ===== */

@keyframes fadeUp {

  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeScale {

  from {
    opacity: 0;
    transform: scale(.96);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* ===== RESPONSIVE ===== */

@media (max-width: 1100px) {

  .hero,
  .problem-grid,
  .solution-grid,
  .micro-grid,
  .benefit-grid,
  .process-grid,
  .support-grid {
    grid-template-columns: 1fr;
  }

  .hero-image img {
    height: 500px;
  }
}

@media (max-width: 768px) {

  section {
    padding: 4rem 1rem;
  }

  .hero {
    min-height: auto;
  }

  .hero-title {
    font-size: 2.8rem;
  }

  .hero-image img {
    height: 350px;
  }
}

</style>